/** @jsx jsx */

import React, { useContext, useRef, useEffect } from 'react';
import { css, jsx } from '@emotion/core';
import Btn from './CarouselBtn';
import Marker from './CarouselMarker';
import { colors } from 'styles/variables';
import { unlockInteraction } from 'services/unlockInteraction';
import { interactionContext as _interactionContext } from './interactionContext';
import { CarouselContext } from './Carousel';
import isEDGE from 'services/isEDGE';

const { lGrey0, lGrey2 } = colors;

enum Direction {
  Prev = 'prev',
  Next = 'next',
}

const CarouselLayout = React.memo(({ children }) => {
  const interactionContext = useContext(_interactionContext);
  const carouselContext = useContext(CarouselContext)!;
  const { currentIndex, animate } = carouselContext;
  const imgHeight = 350;

  const {
    carouselColor = '#fff',
    carouselTextColor = lGrey0,
    animation = 'natural',
  } = interactionContext.props;

  const { layout } = interactionContext.meta;

  const OneColumn = layout.toLowerCase().includes('one');
  // console.log({ OneColumn });

  const itemsSeen = useRef(
    unlockInteraction(interactionContext.id, (children as any).length)
  );

  useEffect(() => {
    if (window.dev()) return;
    if (interactionContext.props.lockDown === 0) return;
    itemsSeen.current(currentIndex);
  });

  return (
    <div
      css={css({
        width: '100%',
        padding: '10px 30px',

        ...(!OneColumn && {
          '@media (min-width: 768px)': {
            maxWidth: '50vw',
          },
        }),
      })}
    >
      <div
        css={css`
          position: relative;
          width: 100%;
          background: ${carouselColor};
          border: 1px solid ${lGrey2};
          border-radius: 10px;
          box-shadow: 5px 10px 20px 10px rgba(0, 0, 0, 0.05);
          overflow: hidden;
        `}
      >
        <ul
          css={css`
            display: flex;
            width: 100%;
            height: auto;
            margin: 0;
            padding: 0 !important;
            overflow: hidden;

            ${isEDGE() &&
              !['OneColumn', 'OneColumnReversed'].includes(layout) &&
              `
              max-width: 490px;
        
            `}
          `}
        >
          {(children as any).map((slide, index) =>
            React.cloneElement(slide, {
              index,
              currentIndex,
              animate,
              imgHeight,
              carouselColor,
              carouselTextColor,
              animation,
              slide,
            })
          )}
        </ul>

        <span className="sr-only">
          Showing slide {currentIndex + 1} of {React.Children.count(children)}:
        </span>

        <div
          css={css`
            position: absolute;
            display: flex;
            justify-content: center;
            width: 100%;
            top: ${imgHeight - 25}px;
          `}
        >
          <div
            css={css`
              display: flex;
            `}
          >
            {React.Children.map(children, (_, i) => (
              <Marker
                key={`marker-${i}`}
                index={i}
                current={currentIndex === i}
              />
            ))}
          </div>
        </div>

        <Btn
          dir={Direction.Prev}
          size={40}
          top={`${imgHeight / 2}px`}
          height={imgHeight}
          enabled={currentIndex > 0}
          onClick={carouselContext[Direction.Prev]}
        />
        <Btn
          dir={Direction.Next}
          size={40}
          top={`${imgHeight / 2}px`}
          height={imgHeight}
          enabled={currentIndex < React.Children.count(children) - 1}
          onClick={carouselContext[Direction.Next]}
        />
      </div>
    </div>
  );
});

export default CarouselLayout;
